import { useState, useEffect, useRef } from "react";
import Skeleton from "@mui/material/Skeleton";
import { ReactComponent as SelectedIcon } from "../../svgs/selected.svg";
import { ReactComponent as UnselectedIcon } from "../../svgs/unselected.svg";
import { ReactComponent as PartiallySelected } from "../../svgs/partially-selected.svg";
import { ReactComponent as AscIcon } from "../../svgs/asc-sort.svg";
import Pagination from "@mui/material/Pagination";
import {Box,Checkbox, PaginationItem, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, TableSortLabel, TextField, Typography} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import isNullOrUndefined from '../functions/isNullOrUndefined'; 

const OrderedDataTableCopy = ({
  RowComponent,
  tableHeader,
  dataList,
  numToPage,
  overflowFlag,
  showSpinner,
  skeletonHeight,
  overrideMessage = false,
  sortingEnabled = false, // Sortable columns
  selectAllStatus = null, // Selected All Status
  onSelectAll = null, // On Select All Click
  selectedRowsList = null, // Context selected rows list
  FooterComponent = null, // Footer Component
  footerObj = null, // Footer Obj
  disableMaxTableHeight = false, // Maximum table height, true to make vertical scroll disappear
  varHeight = "390px", // Height of content other than the table, since max-height is calculated as cal('100vh-x'), x = varHeight
  stickyFirstColumnEnabled = false, // Make true to enable first column left sticky
  setShowSortedDataList,
  showSortedDataList,
  selectableRows = false, // Make true to enable the whole row selection
}) => {
  // initialize state
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState(1);
  const [pageList, setPageList] = useState([]);
  const [showDataList, setShowDataList] = useState([]);
  const [tableCss, setTableCss] = useState("table");
  const [numOfPages, setNumOfPages] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [sortClicks, setSortClicks] = useState(0);
  // initialize ref
  const count = useRef(0);
  // Sorting
  function descendingComparator(a, b, orderBy) {
    // Handle missing orderBy parameter
    if (orderBy === null || orderBy === '' || orderBy === undefined) {
      return -1;
    }
    
    // Check if both values are empty/null/undefined
    var aEmpty = a[orderBy] == null || a[orderBy] === '';
    var bEmpty = b[orderBy] == null || b[orderBy] === '';
    
    // If both are empty, consider them equal
    if (aEmpty && bEmpty) {
      return 0;
    }
    
    // If only one is empty, empty values come last in descending order
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    
    var valueA = a[orderBy];
    var valueB = b[orderBy];
    var typeA = typeof valueA;
    var typeB = typeof valueB;
    
    // Handle dates
    var isDateA = false;
    var isDateB = false;
    var dateA = null;
    var dateB = null;
    
    // Check if values are date objects
    if (valueA instanceof Date) {
      isDateA = true;
      dateA = valueA;
    }
    if (valueB instanceof Date) {
      isDateB = true;
      dateB = valueB;
    }
    
    // Check if strings are MM-DD-YYYY or MM/DD/YYYY format dates
    if (typeA === 'string' && typeof valueA.match === 'function') {
      // Check for MM-DD-YYYY format
      if (valueA.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
        var partsA = valueA.split('-');
        if (partsA.length === 3) {
          // Convert MM-DD-YYYY to a proper Date object
          dateA = new Date(partsA[2], parseInt(partsA[0])-1, parseInt(partsA[1]));
          // To Double check
          if (!isNaN(dateA.getTime())) {
            isDateA = true;
          }
        }
      } 
      // Check for MM/DD/YYYY format
      else if (valueA.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        var partsA = valueA.split('/');
        if (partsA.length === 3) {
          // Convert MM/DD/YYYY to a proper Date object
          dateA = new Date(partsA[2], parseInt(partsA[0])-1, parseInt(partsA[1]));
          // To Double check
          if (!isNaN(dateA.getTime())) {
            isDateA = true;
          }
        }
      }
    }
    
    if (typeB === 'string' && typeof valueB.match === 'function') {
      // Check for MM-DD-YYYY format
      if (valueB.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
        var partsB = valueB.split('-');
        if (partsB.length === 3) {
          // Convert MM-DD-YYYY to a proper Date object
          dateB = new Date(partsB[2], parseInt(partsB[0])-1, parseInt(partsB[1]));
          if (!isNaN(dateB.getTime())) {
            isDateB = true;
          }
        }
      } 
      // Check for MM/DD/YYYY format
      else if (valueB.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        var partsB = valueB.split('/');
        if (partsB.length === 3) {
          // Convert MM/DD/YYYY to a proper Date object
          dateB = new Date(partsB[2], parseInt(partsB[0])-1, parseInt(partsB[1]));
          if (!isNaN(dateB.getTime())) {
            isDateB = true;
          }
        }
      }
    }
    
    // If both values are dates
    if (isDateA && isDateB) {
      return dateB - dateA;
    }
    
    // Handle numbers and numeric strings
    var isNumericA = typeA === 'number' || (typeA === 'string' && !isNaN(Number(valueA)));
    var isNumericB = typeB === 'number' || (typeB === 'string' && !isNaN(Number(valueB)));
    
    if (isNumericA && isNumericB) {
      return Number(valueB) - Number(valueA);
    }
    
    // Handle booleans
    if (typeA === 'boolean' && typeB === 'boolean') {
      return (valueB === valueA) ? 0 : (valueB ? -1 : 1);
    }
    
    // Handle objects (non-Date, non-Array)
    if (typeA === 'object' && typeB === 'object') {
      var strA = JSON.stringify(valueA);
      var strB = JSON.stringify(valueB);
      if (strB < strA) return -1;
      if (strB > strA) return 1;
      return 0;
    }
    
    // Default string comparison for different types or strings
    if (typeA === 'string' && typeB === 'string') {
      // Case-insensitive comparison
      var lowerA = valueA.toLowerCase();
      var lowerB = valueB.toLowerCase();
      
      if (lowerB < lowerA) return -1;
      if (lowerB > lowerA) return 1;
      
      // If strings are equal ignoring case, use case-sensitive comparison for stable sorting
      if (valueB < valueA) return -1;
      if (valueB > valueA) return 1;
      return 0;
    }
    
    // If types are different and not handled above, convert to string
    var strA = String(valueA);
    var strB = String(valueB);
    if (strB < strA) return -1;
    if (strB > strA) return 1;
    return 0;
  }


  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => (-1) * descendingComparator(a, b, orderBy);
  }

  // Function to calculate pagination start and end values
  function calculatePagination(totalItems, itemsPerPage, currentPage) {
    const start = (currentPage - 1) * itemsPerPage + 1;
    let end = currentPage * itemsPerPage;
    if (end > totalItems) {
        end = totalItems;
    }
    return { start, end };
}

  // function to make count equal to 0
  const zeroCount = () => {
    count.current = 0;
  };

  // increment count by 1
  const incrementCount = () => {
    count.current = count.current + 1;
  };

  // event handler when page changes due to pagination component click
  const onPageChange = (event, value) => {
    setPage((page) => (page = parseInt(value)));
    setPageInput((page) => (page = parseInt(value)));
  };

  // Sorting Handler
  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  // Sorting
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    const newSortClicks = orderBy === property ? sortClicks + 1 : 1;

    if (newSortClicks === 3) {
      setOrderBy("");
      setOrder("asc");
      setSortClicks(0);
    } else {
      setOrder(newOrder);
      setOrderBy(property);
      setSortClicks(newSortClicks);
    }
  };

  const handlePageInputChange = (event) => {
    const value = event.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = parseInt(value) || '';
      setPageInput(numValue);
    }
  };
  
  const handlePageInputKeyPress = (event) => {
    if (event.key === 'Enter') {
      // Return early if pageInput is empty or not a number
      if (!pageInput || isNaN(pageInput)) {
        // Reset to current page
        setPageInput(page);
        return;
      }
  
      let newPage = parseInt(pageInput);
      if (newPage < 1) {
        newPage = 1;
      } else if (newPage > numOfPages) {
        newPage = numOfPages;
      }
      setPage(newPage);
      setPageInput(newPage);
    }
  };
  
  const handlePageInputBlur = () => {
    if (!pageInput || isNaN(pageInput)) {
      setPageInput(page);
    }
  };
  

  // useEffect to run on change of dataList state variable
  useEffect(() => {
    setPage((page) => (page = 1));
    setPageInput((page) => (page = 1));
    // determine number of pages and the page list that will
    if (numToPage) {
      let temp = parseInt(dataList.length / numToPage);
      if (dataList.length % numToPage > 0) {
        temp = temp + 1;
      }
      setNumOfPages((numOfPages) => (numOfPages = temp));
      let tempList = [];
      for (let i = 0; i < temp; i++) {
        tempList.push(i + 1);
      }
      setPageList((pageList) => (pageList = tempList));
    }
    // if overflow flag is true allow horizontal scroll
    if (overflowFlag) {
      setTableCss((tableCss) => (tableCss = "table-overflow"));
    }
  }, [dataList]);

  //Set Default Sort Column
  // useEffect(() => {
  //   setOrderBy(sortingEnabled ? tableHeader[0]?.columnname : "");
  // }, [tableHeader]);

  // useEffect to update data to display on page of table when dataList or page state variables change
  useEffect(() => {
    let defaultSortedList = [...dataList];
    if (numToPage) {
      let startIndex = (page - 1) * numToPage;
      setShowDataList(
        showDataList =>
          sortingEnabled ?
          (showDataList = [
            ...defaultSortedList
              .sort(getComparator(order, orderBy))
              .slice(startIndex, startIndex + numToPage),
          ]):
          (showDataList = [
            ...defaultSortedList
              .slice(startIndex, startIndex + numToPage),
          ])
      );
    } else {
      setShowDataList(
        showDataList =>
          sortingEnabled?
          (showDataList = defaultSortedList.sort(getComparator(order, orderBy))):
          (showDataList = defaultSortedList)
      );
    }

    // sort for export 
    if (!isNullOrUndefined(setShowSortedDataList) && !isNullOrUndefined(showSortedDataList)){
      setShowSortedDataList( 
        showSortedDataList => 
            sortingEnabled ? 
              (showSortedDataList = defaultSortedList.sort(getComparator(order, orderBy))):
              (showSortedDataList = defaultSortedList)
      ); 
    }
    
  }, [page, dataList, order, orderBy]);

  // useEffect(() => {
  //   if(showSortedDataList) {
  //     setShowSortedDataList(
  //       (showSortedDataList) =>
  //         showSortedDataList = showDataList
  //     )
  //   }
  // },[showDataList])

  // Return true if row is checked
  const isRowChecked = (mastercompanyId) => {
    /* Check if select all check box is enabled */
    if (onSelectAll===null || selectAllStatus===null || selectedRowsList === null) {
      return false;
    }
    let returnBool = false;
    selectedRowsList.forEach((el) => {
      if(el.mastercompanyid===mastercompanyId){
        returnBool = true;
      }
    });
    return returnBool;
  };
  
  return (
    <>
      {!showSpinner ? (
        <>
          <Paper>
            <TableContainer sx={{ maxHeight: disableMaxTableHeight ?  "none": "calc(100vh - "+varHeight+")", overflow: "overlay"}}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {tableHeader.map((el) => (
                      <TableCell
                        className={stickyFirstColumnEnabled && el['columnorder']===0? "table-cell-sticky-header": ""}
                        sx={{
                          backgroundColor:
                            (el["columnname"] === "checkbox" && onSelectAll!==null && selectAllStatus!==null && selectedRowsList!=null) /* Check if select all check box is enabled */
                              // ? "#005F8D"
                              // : "#CCDBE3",
                              ? process.env.REACT_APP_DARK_COLOR
                              : process.env.REACT_APP_PRIMARY_COLOR, 
                          color: `${process.env.REACT_APP_HEADER_TEXT_COLOR} !important`, 
                          borderRight: "1px solid white !important",
                        }}
                        key={el["columnname"]}
                        sortDirection={
                          orderBy === el["columnname"] && sortingEnabled ? order : false
                        }
                      >
                        {(el["columnname"] === "checkbox" && onSelectAll!==null && selectAllStatus!==null && selectedRowsList!=null) /* Check if select all check box is enabled */ ? ( 
                          <Checkbox
                            sx={{ padding: 0 }}
                            checked={selectAllStatus}
                            onChange={onSelectAll}
                            checkedIcon={<SelectedIcon />}
                            icon={
                              selectedRowsList.length > 0 ? (
                                <PartiallySelected />
                              ) : <UnselectedIcon />
                            }
                          />
                        ) : (
                          sortingEnabled ? /* Check if sorting is enabled */
                          <TableSortLabel
                            active={orderBy === el["columnname"]}
                            direction={
                              orderBy === el["columnname"] ? order : "asc"
                            }
                            onClick={createSortHandler(el["columnname"])}
                            IconComponent={AscIcon}
                          >
                            {el["columntag"]}
                            {orderBy === el["columnname"] ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                          : el["columntag"]
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* zero count at the start of each row */}
                  {zeroCount()}
                  {showDataList.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor:
                          index % 2 !== 0 ? "#F7F9FA" : "#FFFFFF",
                      }}
                      className={selectableRows? "table-row-clicky" : ""}
                      selected={isRowChecked(row.mastercompanyid)}
                    >
                      {/* apply row component function to each row object */}
                      {/* pass table headers to row component so order can be held */}
                      {RowComponent(row, count.current, tableHeader,stickyFirstColumnEnabled,index,selectableRows)}
                      {/* increment count for each element in row */}
                      {incrementCount()}
                    </TableRow>
                  ))}
                </TableBody>
                {footerObj !== null && FooterComponent !== null && 
                tableHeader.length > 0 && dataList.length != 0 && (
                  <TableFooter>
                    <TableRow>
                      {FooterComponent(tableHeader, footerObj,stickyFirstColumnEnabled)}
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </TableContainer>
            {!showSpinner &&
            !overrideMessage &&
            dataList.length == 0 &&
            tableHeader.length > 0 ? (
              <div className="d-flex flex-column justify-content-end align-items-center p-2">
                <ErrorOutlineIcon sx={{fontSize:"40px"}}/>
                <Typography>No Data Available.</Typography>
              </div>
            ) : (
              numToPage && numOfPages > 1 ? <div className="d-flex justify-content-end align-items-center p-2">
                <Typography
                  fontSize={"12px"}
                  paddingRight={"8px"}
                  fontWeight={"400"}
                  lineHeight={"16px"}
                >
                  {calculatePagination(dataList.length, numToPage, page).start +
                    "-" +
                    calculatePagination(dataList.length, numToPage, page).end +
                    " of " +
                    dataList.length +
                    " items"}
                </Typography>
                <Pagination
                  page={page}
                  count={numOfPages}
                  variant="outlined"
                  shape="rounded"
                  onChange={onPageChange}
                  renderItem={(item) => (
                    <PaginationItem
                      {...item}
                      components={{
                        previous: () => (
                          <Typography fontSize={"12px"}>Previous</Typography>
                        ),
                        next: () => (
                          <Typography fontSize={"12px"}>Next</Typography>
                        ),
                      }}
                    />
                  )}
                />
                <Box className="d-flex align-items-center ms-2 me-1">
                  <Typography fontSize={"12px"} className="me-2">
                    Page
                  </Typography>
                  <TextField
                    size="small"
                    value={pageInput}
                    onChange={handlePageInputChange}
                    onKeyPress={handlePageInputKeyPress}
                    onBlur={handlePageInputBlur}
                    inputProps={{
                      style: { 
                        padding: '7px 6px',
                        width: '50px',
                        textAlign: 'center',
                        fontSize: '12px'
                      },
                      'aria-label': 'Jump to page'
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        borderColor: '#00A6CA',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00A6CA'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00A6CA'
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        color: '#00A6CA',
                        '&:hover': {
                          color: '#00A6CA'
                        }
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#00A6CA'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#00A6CA'
                      },
                      '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#BDBDBD'
                      },
                      '& .Mui-disabled input': {
                        color: '#BDBDBD',
                        backgroundColor: '#FCFCFC'
                      }
                    }}
                  />
                  <Typography fontSize={"12px"} sx={{ ml: 1 }}>
                    of {numOfPages}
                  </Typography>
                </Box>
              </div> :  null
            )}
          </Paper>
        </>
      ) : (
        <>
          {/* show skeleton while showSpinner prop is true */}
          {/* generally used for waiting for api response */}
          <br />
          {/* <CircularProgress /> */}
          {/* <LinearProgress sx={{ height: '10px', width: '90%', margin: 'auto', borderRadius:'5px'}} /> */}
          <Skeleton
            variant="rectangular"
            width="100%"
            height="7vh"
            sx={{ borderRadius: "10px", "margin-bottom": "3px" }}
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={skeletonHeight}
            sx={{ borderRadius: "10px" }}
          />
          <br />
        </>
      )}
    </>
  );
};

export default OrderedDataTableCopy;
